import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AccountService } from '../../../../lib/services/account-service/account-service.service';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { AccountState } from '../../../../lib/state/account.state';
import { Store } from '@ngxs/store';
import {
  BaseCssProperties,
  PaymentProvider,
  ProviderClass,
  TextItemWidgetConfig,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { firstValueFrom } from 'rxjs';
import { loadAirwallexComponentsSdk } from '../../../../lib/utils/airwallex-components-sdk.util';

@Component({
  selector: 'finxone-platform-airwallex-rfi-widget',
  templateUrl: './airwallex-rfi-widget.component.html',
  styleUrls: ['./airwallex-rfi-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgStyle],
})
export class AirwallexRfiWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  // Not `static: true`: the host sits inside an `@if`, and static queries never see embedded views.
  @ViewChild('rfiHost') rfiHost?: ElementRef<HTMLDivElement>;

  attri: UiZoneWidgetAttributeConfig;
  contentStyle: baseCssConfigStyle;
  message = '';
  error = '';
  loading = true;
  hidden = false;

  private element?: { unmount?: () => void };
  private shouldMount = false;

  constructor(
    private readonly configService: ConfigService,
    public dataIn: ProviderClass<TextItemWidgetConfig>,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    this.attri = this.dataIn?.attri;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
    ]);
    this.message = this.attri?.widgetProperties?.textContent || 'Additional information is required.';
    const provider = this.store.selectSnapshot(
      (state: { projectSettings?: { provider?: string } }) => state.projectSettings?.provider,
    );
    if (provider !== PaymentProvider.AIRWALLEX) {
      this.hidden = true;
      this.loading = false;
      return;
    }
    this.shouldMount = true;
  }

  // Mounting waits for the view: the host div only exists once `@if (!hidden)` has rendered.
  async ngAfterViewInit() {
    if (!this.shouldMount) {
      return;
    }
    try {
      await this.mountAirwallexRfi();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Unable to load the verification form.';
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.element?.unmount?.();
  }

  private async mountAirwallexRfi() {
    const profile = this.store.selectSnapshot(ProfileState.getProfile);
    const selected = this.store.selectSnapshot(AccountState.getCurrentAccount);
    const organisationId = profile?.activeOrganisationId;
    const userId = profile?.id;
    if (!organisationId || !userId) {
      throw new Error('Sign in to complete this request.');
    }

    const authorize = await firstValueFrom(
      this.accountService.authorizeAirwallexRfi({
        organisationId,
        userId,
        accountId: selected?.accountId,
        connectedAccountId: selected?.attributes?.['connectedAccountId'],
      }),
    );

    const sdk = await loadAirwallexComponentsSdk();

    await sdk.init({
      elements: ['kycRfi'],
      langKey: 'en',
      // Must match the environment that minted the auth code, not a hardcoded guess.
      env: authorize.env ?? 'prod',
      clientId: authorize.clientId,
      authCode: authorize.authCode,
      codeVerifier: authorize.codeVerifier,
    });
    const element = await sdk.createElement('kycRfi', { hideHeader: true });
    const host = this.rfiHost?.nativeElement;
    if (!host) {
      throw new Error('RFI container is missing.');
    }
    element.mount(host);
    this.element = element;
    this.loading = false;
  }
}
